import { Box, Button, Typography } from '@mui/material';
import { PCT_TONES, pctTone } from './effortsTableStyles.js';

// Re-export the styling constants so consumers import everything from one path.
export {
  TABLE_CARD_SX,
  TABLE_WRAP_SX,
  HEADER_CELL_SX,
  TABLE_SX,
  FIRST_COL_SX,
  NUM_CELL_SX,
  SUBPILL_GROUP_SX,
  UNAVAIL_HEADER_SX,
  UNAVAIL_CELL_SX,
  TOTAL_ROW_SX,
} from './effortsTableStyles.js';

/**
 * Shared table components for the Efforts tabs (card title + pills). Values are
 * copied from the SalesPulse reference. Styling only — no behaviour.
 */

/**
 * Large bold title with an optional count pill on the right.
 * @param {{ title: string, count?: number, countLabel?: string }} props
 */
export function TableCardTitle({ title, count, countLabel = 'rows' }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        mb: 1.5,
      }}
    >
      <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#0F1E3D' }}>
        {title}
      </Typography>
      {count != null ? (
        <Box
          component="span"
          sx={{
            flex: '0 0 auto',
            px: 1,
            py: '2px',
            borderRadius: '999px',
            fontSize: '11.5px',
            fontWeight: 700,
            bgcolor: '#eef4ff',
            color: '#2554a8',
            border: '1px solid #bcd5fb',
          }}
        >
          {count} {countLabel}
        </Box>
      ) : null}
    </Box>
  );
}

/** Small grey pill (e.g. designation). */
export function GreyPill({ children }) {
  if (children == null || children === '') return <>—</>;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1,
        py: '2px',
        borderRadius: '999px',
        fontSize: '11.5px',
        fontWeight: 600,
        bgcolor: '#f1f5f9',
        color: '#475569',
        border: '1px solid #e2e8f0',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Simple table pager footer: "Showing X-Y of N", Prev/Next, page X of Y.
 * The reference HTML has no dedicated pagination CSS (its `.tbl tr.total` is a
 * totals row), so this is a plain footer styled to sit under the table box.
 * Renders nothing when there is only one page.
 *
 * @param {{ page: number, pageSize: number, total: number, onPageChange: (p:number)=>void }} props
 *   `page` is zero-based.
 */
export function TableFooter({ page, pageSize, total, onPageChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (pageCount <= 1) return null;
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const start = total === 0 ? 0 : safePage * pageSize + 1;
  const end = Math.min(total, (safePage + 1) * pageSize);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        mt: 1,
        px: 0.5,
      }}
    >
      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
        Showing {start}-{end} of {total}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={safePage <= 0}
          onClick={() => onPageChange(safePage - 1)}
          sx={{ textTransform: 'none', minWidth: 0, px: 1.5 }}
        >
          Prev
        </Button>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0F1E3D' }}>
          Page {safePage + 1} of {pageCount}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={safePage >= pageCount - 1}
          onClick={() => onPageChange(safePage + 1)}
          sx={{ textTransform: 'none', minWidth: 0, px: 1.5 }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Green/amber/red percentage pill using the reference palette. Renders a plain
 * dash when the value is null/non-numeric (no pill, no invented number).
 * @param {{ value: number|null, text?: string, good?: number, warn?: number }} props
 */
export function PctPill({ value, text, good, warn }) {
  const tone = pctTone(value, { good, warn });
  if (!tone) return <>—</>;
  const t = PCT_TONES[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1,
        py: '2px',
        borderRadius: '999px',
        fontSize: '11.5px',
        fontWeight: 600,
        color: t.color,
        bgcolor: t.bg,
        border: `1px solid ${t.border}`,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {text ?? `${Number(value).toFixed(1)}%`}
    </Box>
  );
}

/**
 * COLOURED-TEXT value (not a pill, not a coloured cell). Renders `children` in
 * `color` with bold weight; when `color` is falsy the text inherits. Use for
 * coverage/compliance/achievement values the brief wants as coloured text.
 * @param {{ color?: string, children: React.ReactNode }} props
 */
export function ColoredText({ color, children }) {
  return (
    <Box
      component="span"
      sx={{
        color: color || 'inherit',
        fontWeight: color ? 800 : 400,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Greyed em dash for a control/column that has no data available. Styled to
 * match UNAVAIL_CELL_SX so an "unavailable" cell reads as deliberately empty.
 */
export function GreyDash() {
  return <Box component="span" sx={{ color: '#b4bcc8' }}>—</Box>;
}
